import { createContext, useContext } from 'react';
import { useFocusRing as useReactAriaFocusRing } from 'react-aria';
import { useTheme } from 'foundations-theming/utils';
export const FocusableParentContext = /*#__PURE__*/createContext({
  delegateFocus: false
});

/**
 * Determines whether a focus ring should be shown on the focused element.
 * @example
 * const StyledButton = styled.button(({ $focusRingStyles }) => {...$focusRingStyles});
 *
 * const MyButton = () => {
 *   const {focusProps, focusRingStyles} = useTrellisFocusRing();
 *
 *   // Be sure to merge existing component props with `focusProps` as needed
 *   return <StyledButton {...focusProps} $focusRingStyles={focusRingStyles} />
 * };
 * @param props - Focus ring config.
 * @returns Focus ring state and styles to apply to the focused element. Spread the `focusProps` onto the element that will receive focus. Pass `focusRingStyles` to a Styled Component.
 */
export const useTrellisFocusRing = props => {
  const result = useReactAriaFocusRing(props);
  const theme = useTheme();
  const {
    delegateFocus
  } = useContext(FocusableParentContext);
  const condition = props !== null && props !== void 0 && props.isTextInput ? result.isFocused : result.isFocusVisible;
  const style = delegateFocus ? {
    outline: 'none'
  } : {
    outlineWidth: theme.borderWidth[200],
    outlineStyle: 'solid',
    outlineOffset: theme.space[25],
    outlineColor: theme.color['border-primary-subtle-pressed']
  };
  return Object.assign({}, result, {
    focusRingStyles: condition ? style : {}
  });
};